import {
  Document, Packer, Paragraph, TextRun, ImageRun,
  Table, TableRow, TableCell,
  WidthType, AlignmentType, BorderStyle,
  ShadingType, HeadingLevel,
} from 'docx';
import { buildTransitionTable, buildPdaTransitionList, buildTmTransitionList } from './automataHelpers';

const FONT = 'Aptos';
const MONO = 'Consolas';
const ACCENT = '3B82F6';
const GREEN = '16A34A';
const BLUE = '2563EB';
const ORANGE = 'EA580C';
const RED = 'DC2626';
const GREEN_BG = 'DCFCE7';
const BLUE_BG = 'DBEAFE';
const ORANGE_BG = 'FED7AA';
const RED_BG = 'FEE2E2';

const thinBorder = { style: BorderStyle.SINGLE, size: 1, color: 'D1D5DB' };
const cellBorders = { top: thinBorder, bottom: thinBorder, left: thinBorder, right: thinBorder };

function headerCell(text) {
  return new TableCell({
    children: [new Paragraph({
      children: [new TextRun({ text, font: MONO, bold: true, size: 20, color: 'FFFFFF' })],
      alignment: AlignmentType.CENTER,
    })],
    shading: { type: ShadingType.SOLID, color: ACCENT },
    borders: cellBorders,
    verticalAlign: 'center',
  });
}

function dataCell(text, bold = false) {
  return new TableCell({
    children: [new Paragraph({
      children: [new TextRun({ text, font: MONO, size: 20, bold })],
      alignment: bold ? AlignmentType.LEFT : AlignmentType.CENTER,
    })],
    borders: cellBorders,
    verticalAlign: 'center',
  });
}

function sanitizeName(name) {
  return (name || 'automaton').replace(/[^a-zA-Z0-9_\- ]/g, '_').replace(/\s+/g, '_');
}

/**
 * Export a complete automaton as a formatted Word document.
 * @param {Object} automaton - The automaton data
 * @param {Object|null} user - User object with name/email (null for guests)
 * @param {Blob|null} imageBlob - Canvas image blob (null if not available)
 */
export async function exportAutomatonAsDocx(automaton, user, imageBlob) {
  const children = [];

  // Title: Automaton name
  children.push(new Paragraph({
    children: [new TextRun({ text: automaton.name || 'Untitled Automaton' })],
    heading: HeadingLevel.TITLE,
  }));

  // Subtitle: User name
  const userName = user?.name || user?.email || 'Guest';
  children.push(new Paragraph({
    children: [new TextRun({
      text: userName,
      font: FONT,
      size: 22,
      color: '6B7280',
    })],
    spacing: { after: 200 },
  }));

  // --- Language Description section ---
  const langDesc = automaton.language_description || '';
  children.push(sectionHeading('Language Description'));
  children.push(new Paragraph({
    children: [new TextRun({
      text: langDesc || 'No description provided.',
      font: FONT,
      size: 22,
      italics: !langDesc,
      color: langDesc ? '374151' : '9CA3AF',
    })],
    spacing: { after: 200 },
  }));

  // --- Specification section ---
  children.push(sectionHeading('Specification'));
  const s = automaton;
  const q = s.states?.length > 0 ? `{${s.states.map(st => st.name).join(', ')}}` : '∅';
  const sigma = s.alphabet?.length > 0 ? `{${s.alphabet.join(', ')}}` : '∅';
  const q0 = s.states?.find(st => st.is_initial)?.name || '—';
  const finals = s.states?.filter(st => st.is_final).map(st => st.name) || [];
  const f = finals.length > 0 ? `{${finals.join(', ')}}` : '∅';

  const specItems = [['Q', q], ['Σ', sigma], ['q', q0], ['F', f]];

  // Add PDA-specific spec items
  if (automaton.type === 'PDA') {
    const LAMBDA = '\u03BB';
    const epsilonSymbols = new Set(['ε', 'λ', LAMBDA]);
    const stackSymbols = new Set();
    const z0 = automaton.initial_stack_symbol || 'Z';
    stackSymbols.add(z0);
    for (const t of (automaton.transitions || [])) {
      for (const r of (t.pda_rules || [])) {
        if (r.pop && !epsilonSymbols.has(r.pop)) stackSymbols.add(r.pop);
        if (r.push) {
          for (const ch of r.push) {
            if (!epsilonSymbols.has(ch)) stackSymbols.add(ch);
          }
        }
      }
    }
    const gamma = stackSymbols.size > 0 ? `{${[...stackSymbols].sort().join(', ')}}` : '∅';
    // Insert PDA-specific items after Σ
    specItems.splice(2, 0, ['Γ', gamma]);
    specItems.splice(4, 0, ['z', z0]);
  }

  // Add TM-specific spec items
  if (automaton.type === 'TM') {
    const tapeSymbols = new Set(automaton.alphabet || []);
    const blankSym = automaton.blank_symbol || '\u25A1';
    tapeSymbols.add(blankSym);
    for (const t of (automaton.transitions || [])) {
      for (const r of (t.tm_rules || [])) {
        if (r.read) tapeSymbols.add(r.read);
        if (r.write) tapeSymbols.add(r.write);
      }
    }
    const gamma = tapeSymbols.size > 0 ? `{${[...tapeSymbols].sort().join(', ')}}` : '\u2205';
    const acceptStates = (automaton.states || []).filter(st => st.is_final).map(st => st.name);
    const rejectStates = (automaton.states || []).filter(st => st.is_reject).map(st => st.name);
    const qAcc = acceptStates.length > 0 ? acceptStates.join(', ') : '\u2014';
    const qRej = rejectStates.length > 0 ? rejectStates.join(', ') : '\u2014';
    // Replace F with TM-specific items
    specItems.pop(); // remove F
    specItems.splice(2, 0, ['\u0393', gamma]);
    specItems.push(['q_acc', qAcc]);
    specItems.push(['q_rej', qRej]);
    specItems.push(['\u25A1', blankSym]);
    specItems.push(['Limit', String(automaton.step_limit || 10000)]);
  }

  specItems.forEach(([label, value]) => {
    children.push(new Paragraph({
      children: [new TextRun({ text: `${label}  =  ${value}`, font: MONO, size: 21 })],
      spacing: { after: 60 },
    }));
  });

  children.push(new Paragraph({ spacing: { after: 120 } }));

  // --- Transition Function section ---
  children.push(sectionHeading('Transition Function'));

  const states = automaton.states || [];
  const transitions = automaton.transitions || [];

  if (!states.length || !transitions.length) {
    children.push(new Paragraph({
      children: [new TextRun({ text: 'No transitions defined.', font: FONT, size: 22, italics: true, color: '9CA3AF' })],
      spacing: { after: 200 },
    }));
  } else if (automaton.type === 'PDA') {
    // PDA: list format table
    const rows = buildPdaTransitionList(states, transitions);
    if (rows.length === 0) {
      children.push(new Paragraph({
        children: [new TextRun({ text: 'No transitions defined.', font: FONT, size: 22, italics: true, color: '9CA3AF' })],
        spacing: { after: 200 },
      }));
    } else {
      const headerRow = new TableRow({
        tableHeader: true,
        children: ['State', 'Input', 'Pop', '\u2192', 'Next', 'Push'].map(h => headerCell(h)),
      });
      const dataRows = rows.map(r => new TableRow({
        children: [
          dataCell(r.from, true), dataCell(r.input), dataCell(r.pop),
          dataCell('\u2192'), dataCell(r.to, true), dataCell(r.push),
        ],
      }));
      children.push(new Table({
        rows: [headerRow, ...dataRows],
        width: { size: 0, type: WidthType.AUTO },
      }));
      children.push(new Paragraph({ spacing: { after: 200 } }));
    }
  } else if (automaton.type === 'TM') {
    // TM: list format table
    const rows = buildTmTransitionList(states, transitions);
    if (rows.length === 0) {
      children.push(new Paragraph({
        children: [new TextRun({ text: 'No transitions defined.', font: FONT, size: 22, italics: true, color: '9CA3AF' })],
        spacing: { after: 200 },
      }));
    } else {
      const headerRow = new TableRow({
        tableHeader: true,
        children: ['State', 'Read', '\u2192', 'Write', 'Next', 'Move'].map(h => headerCell(h)),
      });
      const dataRows = rows.map(r => new TableRow({
        children: [
          dataCell(r.from, true), dataCell(r.read),
          dataCell('\u2192'), dataCell(r.write), dataCell(r.to, true), dataCell(r.direction),
        ],
      }));
      children.push(new Table({
        rows: [headerRow, ...dataRows],
        width: { size: 0, type: WidthType.AUTO },
      }));
      children.push(new Paragraph({ spacing: { after: 200 } }));
    }
  } else {
    const { lookup, columns } = buildTransitionTable(states, transitions);
    if (columns.length === 0) {
      children.push(new Paragraph({
        children: [new TextRun({ text: 'No transitions defined.', font: FONT, size: 22, italics: true, color: '9CA3AF' })],
        spacing: { after: 200 },
      }));
    } else {
      const headerRow = new TableRow({
        tableHeader: true,
        children: [headerCell('δ'), ...columns.map(sym => headerCell(sym))],
      });

      const dataRows = states.map(state => {
        const label = state.name;
        return new TableRow({
          children: [
            dataCell(label, true),
            ...columns.map(sym => {
              const dests = lookup[`${state.id}|${sym}`];
              return dataCell(dests ? `{${[...dests].join(', ')}}` : '—');
            }),
          ],
        });
      });

      children.push(new Table({
        rows: [headerRow, ...dataRows],
        width: { size: 0, type: WidthType.AUTO },
      }));
      children.push(new Paragraph({ spacing: { after: 200 } }));
    }
  }

  // --- Transition Graph section ---
  children.push(sectionHeading('Transition Graph'));

  if (imageBlob) {
    const imageBuffer = await imageBlob.arrayBuffer();
    // Determine image dimensions for proper aspect ratio in the doc
    const dims = await getImageDimensions(imageBlob);
    const maxWidth = 550; // points (~7.6 inches)
    const scale = Math.min(1, maxWidth / dims.width);
    children.push(new Paragraph({
      children: [new ImageRun({
        data: imageBuffer,
        transformation: {
          width: Math.round(dims.width * scale),
          height: Math.round(dims.height * scale),
        },
        type: 'png',
      })],
      spacing: { after: 100 },
    }));
  } else {
    children.push(new Paragraph({
      children: [new TextRun({
        text: 'Graph image not available (automaton must be open on canvas to include the diagram).',
        font: FONT, size: 22, italics: true, color: '9CA3AF',
      })],
      spacing: { after: 200 },
    }));
  }

  // --- Testing Strings section ---
  const acceptStrings = automaton.test_accept_strings || [];
  const rejectStrings = automaton.test_reject_strings || [];

  if (acceptStrings.length > 0 || rejectStrings.length > 0) {
    children.push(sectionHeading('Testing Strings'));

    // Legend
    const legendRows = [
      { label: 'True Positive', desc: 'should accept, accepts', color: GREEN, bg: GREEN_BG, icon: '\u2713' },
      { label: 'False Positive', desc: 'should reject, accepts', color: RED, bg: RED_BG, icon: '\u2717' },
      { label: 'True Negative', desc: 'should reject, rejects', color: BLUE, bg: BLUE_BG, icon: '\u2713' },
      { label: 'False Negative', desc: 'should accept, rejects', color: ORANGE, bg: ORANGE_BG, icon: '\u2717' },
    ];
    children.push(new Table({
      rows: legendRows.map(({ label, desc, color, bg, icon }) => new TableRow({
        children: [
          new TableCell({
            children: [new Paragraph({
              children: [new TextRun({ text: icon, font: FONT, size: 18, bold: true, color })],
              alignment: AlignmentType.CENTER,
            })],
            shading: { type: ShadingType.SOLID, color: bg },
            borders: cellBorders,
            width: { size: 8, type: WidthType.PERCENTAGE },
          }),
          new TableCell({
            children: [new Paragraph({
              children: [
                new TextRun({ text: label, font: FONT, size: 18, bold: true, color }),
                new TextRun({ text: `  ${desc}`, font: FONT, size: 18, color: '6B7280' }),
              ],
            })],
            shading: { type: ShadingType.SOLID, color: bg },
            borders: cellBorders,
          }),
        ],
      })),
      width: { size: 0, type: WidthType.AUTO },
    }));
    children.push(new Paragraph({ spacing: { after: 160 } }));

    // Accept Strings
    if (acceptStrings.length > 0) {
      children.push(new Paragraph({
        children: [new TextRun({ text: 'Accept Strings' })],
        heading: HeadingLevel.HEADING_2,
      }));
      acceptStrings.forEach(str => {
        const result = simulateString(automaton, str);
        const isCorrect = result === 'accepted';
        const color = isCorrect ? GREEN : ORANGE;
        const bg = isCorrect ? GREEN_BG : ORANGE_BG;
        const icon = isCorrect ? '\u2713' : '\u2717';
        const displayStr = (str === '\u03BB' || str === '\u03B5') ? str : (str || '\u03B5');
        children.push(new Paragraph({
          children: [
            new TextRun({ text: `${icon}  `, font: FONT, bold: true, size: 20, color }),
            new TextRun({ text: displayStr, font: MONO, size: 20, color }),
          ],
          shading: { type: ShadingType.SOLID, color: bg },
          spacing: { after: 40 },
        }));
      });
      children.push(new Paragraph({ spacing: { after: 120 } }));
    }

    // Reject Strings
    if (rejectStrings.length > 0) {
      children.push(new Paragraph({
        children: [new TextRun({ text: 'Reject Strings' })],
        heading: HeadingLevel.HEADING_2,
      }));
      rejectStrings.forEach(str => {
        const result = simulateString(automaton, str);
        const isCorrect = result === 'rejected' || result === 'stuck' || result === 'no-initial';
        const color = isCorrect ? BLUE : RED;
        const bg = isCorrect ? BLUE_BG : RED_BG;
        const icon = isCorrect ? '\u2713' : '\u2717';
        const displayStr = (str === '\u03BB' || str === '\u03B5') ? str : (str || '\u03B5');
        children.push(new Paragraph({
          children: [
            new TextRun({ text: `${icon}  `, font: FONT, bold: true, size: 20, color }),
            new TextRun({ text: displayStr, font: MONO, size: 20, color }),
          ],
          shading: { type: ShadingType.SOLID, color: bg },
          spacing: { after: 40 },
        }));
      });
      children.push(new Paragraph({ spacing: { after: 120 } }));
    }
  }

  const doc = new Document({
    styles: {
      paragraphStyles: [
        {
          id: 'Title',
          name: 'Title',
          basedOn: 'Normal',
          next: 'Normal',
          run: { font: FONT, size: 36, bold: true, color: '111827' },
          paragraph: { spacing: { after: 60 } },
        },
        {
          id: 'Heading1',
          name: 'Heading 1',
          basedOn: 'Normal',
          next: 'Normal',
          run: { font: FONT, size: 26, bold: true, color: '1F2937' },
          paragraph: {
            spacing: { before: 240, after: 120 },
            border: { bottom: { style: BorderStyle.SINGLE, size: 1, color: 'E5E7EB', space: 4 } },
          },
        },
        {
          id: 'Heading2',
          name: 'Heading 2',
          basedOn: 'Normal',
          next: 'Normal',
          run: { font: FONT, size: 22, bold: true, color: '374151' },
          paragraph: { spacing: { before: 120, after: 80 } },
        },
      ],
    },
    sections: [{ children }],
  });
  const blob = await Packer.toBlob(doc);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${sanitizeName(automaton.name)}.docx`;
  a.click();
  URL.revokeObjectURL(url);
}

function sectionHeading(text) {
  return new Paragraph({
    children: [new TextRun({ text })],
    heading: HeadingLevel.HEADING_1,
  });
}

function getImageDimensions(blob) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
      URL.revokeObjectURL(img.src);
    };
    img.onerror = () => resolve({ width: 600, height: 400 });
    img.src = URL.createObjectURL(blob);
  });
}

function simulateString(automaton, rawInput) {
  if (!automaton?.states?.length) return 'no-initial';
  const input = (rawInput === '\u03BB' || rawInput === '\u03B5') ? '' : rawInput;
  const initial = automaton.states.find(s => s.is_initial);
  if (!initial) return 'no-initial';
  let curStates = [initial.id];
  for (let i = 0; i < input.length; i++) {
    const next = new Set();
    for (const sid of curStates) {
      for (const t of automaton.transitions) {
        if (t.from_state === sid && t.symbols.includes(input[i])) next.add(t.to_state);
      }
    }
    if (next.size === 0) return 'stuck';
    curStates = [...next];
  }
  return curStates.some(id => automaton.states.find(s => s.id === id)?.is_final)
    ? 'accepted' : 'rejected';
}
